import { useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import React from 'react'
import styled, { css } from 'styled-components'

import { Button, Typography } from '@blade/ui-kit'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import { InviteCode, referralCodeAtom } from '@root/entities/referral'
import { inviteReferralDialogOpenAtom } from '@root/features/referral/atoms/reatom-referral-dialogs'
import { useTranslation } from '@root/shared/localization'
import { Drawer, Separator } from '@root/shared/ui-kit'

import { ReferralCodeActions } from '../../desktop/common/referral-code-actions'
import { ReferralCodeInfo } from '../../desktop/common/referral-code-info'
import { ReferralPointsInfo } from '../../desktop/common/referral-points-info'

export const ViewReferralCodeDrawer = () => {
  const [open, setOpen] = useAtom(inviteReferralDialogOpenAtom)

  return (
    <AnimatePresence>
      {open ? <Inner open={open} onOpenChange={setOpen} /> : undefined}
    </AnimatePresence>
  )
}

type InnerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const Inner: React.FC<InnerProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation()
  const [referralCode] = useAtom(referralCodeAtom)
  const handleClose = () => onOpenChange(false)

  if (!referralCode) {
    return undefined
  }

  const referralLink = DeeplinkURL.fromDeeplink(
    new Deeplink.InviteCodeJoin({ inviteCode: InviteCode(referralCode) }),
  )

  return (
    <Root backgroundGradient="primary" open={open} onOpenChange={onOpenChange}>
      <Content>
        <Header>
          <Title>{t('referral.your-referral-code')}</Title>
          <SubTitle>{t('referral.view-referral-subtitle')}</SubTitle>
          <ReferralCodeInfo
            referralCode={referralCode}
            withLink
            css={css`
              margin-block: 20px 16px;
            `}
          />
          <ReferralCodeActions referralLink={referralLink} />
          <Separator
            css={css`
              margin-top: 22px;
              margin-bottom: 6px;
            `}
          />
        </Header>

        <Body>
          <ReferralPointsInfo withLabel />
          <ButtonClose
            variant="soft"
            size="m"
            radius="rounded"
            onClick={handleClose}
          >
            {t('common.close')}
          </ButtonClose>
        </Body>
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)``

const Content = styled.div`
  padding-bottom: var(--safe-bottom);
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  text-align: center;
  gap: 2px;
`

const Body = styled.div`
  padding: 10px 12px 0;
  display: grid;
`

const Title = styled(Typography).attrs({
  variant: 'h4',
  component: 'h2',
})`
  font-size: 18px;
  line-height: 140%;
  color: var(--text-12);
`

const SubTitle = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'h3',
})`
  color: var(--textA-9);
`

const ButtonClose = styled(Button)`
  margin-top: 18px;
  margin-bottom: 10px;
`
